// One photo slot: a file input, the photos already attached, and whatever is
// mid-flight. Both of the Assembly flow's image affordances render through it —
// the finished-kit photo at the top of KittingFields and the per-step photo
// inside each BuildStepsEditor row — so the two read as the same control and a
// change to the tile, the progress chip or the error affordance lands on both.

import { useEffect, useState, type ChangeEvent } from 'react';

import { PHOTO_CAPTURE_ACCEPT, isRenderableImage, type AssetDto } from 'shared';

import * as uploadsApi from '../api/uploads.js';
import { RemoveIcon } from './ControlIcons.js';
import { readBackValue } from './FieldReadBack.js';
import { usePasteFiles } from './usePasteFiles.js';
import { fileExtension, fileTypeCategory, formatBytes } from './useLineAssets.js';
import { type PendingPhoto } from './useAssemblyPhotos.js';

export type PhotoStripLabels = {
  label: string;
  help?: string;
  loading: string;
  dismiss: string;
  remove: (filename: string) => string;
  // Names the in-flight upload for a screen reader ("Uploading kit.jpg"), so the
  // progress chip is announced as that file's progress and not a bare number.
  uploadProgress?: (filename: string) => string;
  // The canonical "(optional)" marker, rendered beside the label when the SLOT
  // itself is optional. A step photo does not pass it: it already sits inside an
  // optional step, so marking every step row would be noise.
  optionalSuffix?: string;
  // The "paste a copied image" hint. Paste works on every strip; the kit photo
  // alone says so, since repeating it on each step row would be noise.
  pasteHint?: string;
  // The camera control's text. Shown on touch devices only: Chrome on Android 14+
  // can hide the camera behind a plain picker, and on a desktop `capture` is
  // ignored, so there it would be a second copy of the picker.
  takePhoto?: string;
};

export function PhotoStrip({
  inputId,
  accept,
  labels,
  assets,
  pending,
  loading,
  error,
  onPick,
  onRemove,
  onDismiss,
  disabled,
}: {
  inputId: string;
  accept: string;
  labels: PhotoStripLabels;
  assets: readonly AssetDto[];
  pending: readonly PendingPhoto[];
  loading: boolean;
  // This slot's own failure, already narrowed by the caller. A shared message
  // rendered in both slots would tell the customer their step photo failed when
  // it was the kit photo that did.
  error?: string | null;
  onPick: (files: ArrayLike<File> | null) => void;
  onRemove: (asset: AssetDto) => void;
  onDismiss: (local_id: string) => void;
  // Renders the slot as its ANSWER: the photos that were attached, with no way
  // to add or remove one. The file input, the help that asks for a photo, every
  // tile's remove button and an in-flight upload's dismiss are HIDDEN rather than
  // disabled — a picker with nothing behind it is an affordance the customer does
  // not have, and unlike a field it carries no answer of its own to read back
  // (ADR-018 phase 15).
  disabled?: boolean;
}): JSX.Element {
  const locked = disabled === true;
  const pasteProps = usePasteFiles(onPick, !locked);
  const helpId = `${inputId}-help`;
  const pasteHintId = `${inputId}-paste-hint`;
  const errorId = `${inputId}-error`;
  const labelId = `${inputId}-label`;
  const cameraTextId = `${inputId}-camera`;
  const describedBy = [
    labels.help ? helpId : null,
    labels.pasteHint ? pasteHintId : null,
    error ? errorId : null,
  ]
    .filter((v): v is string => v !== null)
    .join(' ');
  // The paste hint means nothing to the camera, which only renders on touch.
  const cameraDescribedBy = [labels.help ? helpId : null, error ? errorId : null]
    .filter((v): v is string => v !== null)
    .join(' ');
  const pickAndReset = (e: ChangeEvent<HTMLInputElement>): void => {
    onPick(e.target.files);
    // Clear the input so re-picking the same file fires a change again.
    e.target.value = '';
  };
  return (
    // Locked, the slot itself carries the field key the input carried, so the
    // key-equals-id contract holds in every state — a photo list and an em-dash
    // are mutually exclusive, and neither renders while the fetch is in flight.
    <div className="photo-slot" {...(locked ? { id: inputId } : pasteProps)}>
      {/* A <span> once the input is gone: `htmlFor` would point at nothing, and
          static text is not a labelable element. Both arms carry
          `.photo-slot__label`, which is what makes a slot holding photos and an
          empty one label identically. */}
      {locked ? (
        <span className="photo-slot__label">
          <span>{labels.label}</span>
          {labels.optionalSuffix && <span className="help"> {labels.optionalSuffix}</span>}
        </span>
      ) : (
        <label className="photo-slot__label" htmlFor={inputId} id={labelId}>
          <span>{labels.label}</span>
          {labels.optionalSuffix && <span className="help"> {labels.optionalSuffix}</span>}
        </label>
      )}
      {!locked && labels.help && (
        <span className="help help--block" id={helpId}>
          {labels.help}
        </span>
      )}
      {!locked && labels.pasteHint && (
        <span className="help help--block" id={pasteHintId}>
          {labels.pasteHint}
        </span>
      )}
      {!locked && (
        <input
          id={inputId}
          type="file"
          multiple
          accept={accept}
          {...(describedBy.length > 0 ? { 'aria-describedby': describedBy } : {})}
          onChange={pickAndReset}
          disabled={disabled}
        />
      )}
      {!locked && labels.takePhoto && (
        // Named "Take photo" plus the slot's own question, so a screen reader
        // can tell one step's camera from the next.
        <label className="photo-slot__camera">
          <input
            type="file"
            accept={PHOTO_CAPTURE_ACCEPT}
            capture="environment"
            className="visually-hidden"
            aria-labelledby={`${cameraTextId} ${labelId}`}
            {...(cameraDescribedBy.length > 0 ? { 'aria-describedby': cameraDescribedBy } : {})}
            onChange={pickAndReset}
          />
          <span id={cameraTextId}>{labels.takePhoto}</span>
        </label>
      )}
      {loading && assets.length === 0 && pending.length === 0 && (
        <p className="help help--flush">{labels.loading}</p>
      )}
      {/* No photo was attached. The fetch is not gated on `disabled`, so this
          waits for it to settle rather than flashing the em-dash over photos that
          are on their way. The slot is a named question, so it reports "none"
          instead of vanishing and leaving the reader unable to tell it was asked
          — the em-dash comes from readBackValue, the one author of that blank. */}
      {locked && !loading && assets.length === 0 && (
        <p className="value--locked">{readBackValue(undefined)}</p>
      )}
      {error && (
        <span className="field-error" id={errorId}>
          {error}
        </span>
      )}
      {(assets.length > 0 || pending.length > 0) && (
        <ul className="photo-list">
          {assets.map((asset) => (
            <PhotoTile
              key={asset.id}
              asset={asset}
              onRemove={() => onRemove(asset)}
              removeLabel={labels.remove(asset.filename)}
              disabled={disabled}
            />
          ))}
          {pending.map((p) => {
            const percent = Math.round(p.progress * 100);
            return (
              <li key={p.local_id} className={`photo${p.error ? ' photo--error' : ''}`}>
                {p.preview_url ? (
                  <img src={p.preview_url} alt="" className="photo__thumb" />
                ) : (
                  <BlankThumb filename={p.filename} />
                )}
                <span className="photo__name">{p.filename}</span>
                {p.error ? (
                  <>
                    <span className="field-error">{p.error}</span>
                    {!locked && (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => onDismiss(p.local_id)}
                      >
                        {labels.dismiss}
                      </button>
                    )}
                  </>
                ) : (
                  // `progressbar`, not a bare number: without the role the
                  // percentage is inert text a screen-reader customer never
                  // hears after picking a file.
                  <span
                    className="photo__meta"
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    {...(labels.uploadProgress
                      ? { 'aria-label': labels.uploadProgress(p.filename) }
                      : {})}
                  >
                    {percent}%
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// The tile for a file the browser cannot draw — a step's BOM spreadsheet, Word
// instruction or Visio flow, or a HEIC — stamped with its extension in the same
// per-type color the artwork tiles use, so it reads as a file and not a gap.
function BlankThumb({ filename }: { filename: string }): JSX.Element {
  return (
    <span
      className="photo__thumb photo__thumb--blank"
      data-filetype={fileTypeCategory(filename)}
      aria-hidden="true"
    >
      {fileExtension(filename)}
    </span>
  );
}

// A photo already on the quote. The bytes come through the same-origin preview
// proxy rather than the signed download URL, the way every other asset thumbnail
// in the SPA does (UploadSlot's AssetTile) — a cross-origin <img src=SAS> is the
// fragile path this codebase already moved off.
//
// Only a format the browser can decode is fetched at all; a HEIC keeps the blank
// tile rather than a broken-image icon, and onError falls back to the same tile
// if a decode fails anyway.
export function PhotoTile({
  asset,
  onRemove,
  removeLabel,
  disabled,
}: {
  asset: AssetDto;
  onRemove: () => void;
  removeLabel: string;
  // The photo is the answer; removing it is an action. Locked, the tile keeps the
  // thumbnail, the filename and the size and drops the button entirely.
  disabled?: boolean;
}): JSX.Element {
  const renderable = isRenderableImage(asset.content_type);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    if (!renderable) return;
    let cancelled = false;
    let created: string | null = null;
    void uploadsApi
      .fetchAssetPreviewBlobUrl(asset.id)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewFailed(true);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [asset.id, renderable]);

  const showImage = previewUrl !== null && !previewFailed;
  return (
    <li className="photo">
      {showImage ? (
        <img
          src={previewUrl}
          alt=""
          className="photo__thumb"
          onError={() => setPreviewFailed(true)}
        />
      ) : (
        <BlankThumb filename={asset.filename} />
      )}
      <span className="photo__name">{asset.filename}</span>
      <span className="photo__meta">{formatBytes(asset.size_bytes)}</span>
      {disabled !== true && (
        <button
          type="button"
          className="icon-button icon-button--danger"
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <RemoveIcon />
        </button>
      )}
    </li>
  );
}
